import { useContext, useEffect, useState, useRef } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { APP_TITLE } from "../../../config/constant";
import LayoutContext from "../../../LayoutContext";
import PopupContext from "../../../PopupContext";
import { defaultStatusColors, getSpanWithColor } from "../../../utils/CardUtils";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import BtnLargeSolid from "../../common/buttons/BtnLargeSolid";
import InfinityGridStoresCards from "../../common/cardContainers/InfinityGridStoresCards";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import ListComponent from "../../common/lists/ListComponent";
import MainWindowHeader, {
    CustomSearch,
    OrganizeBtnMenu
} from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import AppContext from "../../../AppContext";
import { roleAccesse } from "../../../utils/roleManager";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function StoresPage() {
    document.title = `${APP_TITLE} - Stores`;

    // #region STATES
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigations = useSelector(state => state.navigationAccesses);
    const { showIsStoreSavedPopup, setShowIsStoreSavedPopup } = useContext(LayoutContext);
    const { setShowStoreAndPOSOnboardingCompleted, setShowStoreWithoutPOSOnboardingCompleted } =
        useContext(PopupContext);
    const queryClient = useQueryClient();
    const gridRef = useRef(null);

    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdatedDateTime, setLastUpdatedDateTime] = useState("--");
    const [removeID, setRemoveID] = useState(null);
    const [toogleSuspendData, setToogleSuspendData] = useState(null);
    const navigate = useNavigate();
    const labelBrandData = JSON.parse(localStorage.getItem("labelBrands"))?.[0];

    const [listNumRows, setListNumRows] = useState(50);

    const { userData } = useContext(AppContext);
    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");

    //---------- Header Filters
    const [searchText, setSearchText] = useState("");
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [filteredRetailBrand, setFilteredRetailBrand] = useState([]);
    //-----
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const filters = {
        filterRetailBrandIDs: filteredRetailBrand?.map(item => item?.value),
        searchText: searchText
    };

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const infiniteGridQuerykey = ["StoresGrid", filters];
    const listQueryKey = ["StoresList", filters];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);
        setupHeaders(
            props,
            navigations,
            //----
            onClickRefreshAction,
            allRetailBrandsList,
            onRetailBrandChange,
            onFiltersLoaded,
            filteredRetailBrand,
            onSearch,
            editAccess
        );
    }, [props?.layoutType, navigations, allRetailBrandsList, filteredRetailBrand, requestEnabled, editAccess]); // Setup Headers

    useEffect(() => {
        Library.removeQueries(queryClient, infiniteGridQuerykey);
        Library.removeQueries(queryClient, listQueryKey);
    }, []); // Cleanup queries on mount and check for first store
    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Store",
            selector: row => row?.name ?? "--"
        },
        {
            name: "Created by",
            selector: row => row?.createdBy ?? "--"
        },
        {
            name: "Created on",
            selector: row => formatDefaultDateTime(row?.createdOn) ?? "--"
        },
        {
            name: "Company",
            selector: row => row?.companyName ?? "--"
        },
        {
            name: "Retail brand",
            selector: row => row?.retailBrandName ?? "--"
        },
        {
            name: "POS",
            selector: row => row?.POSsystem ?? "--"
        },
        {
            name: "POS status",
            selector: row => getSpanWithColor(row?.POSstatus ?? "--", defaultStatusColors)
        },
        {
            name: "Deliveries",
            selector: row => deliveriesStatus(row) ?? "--"
        }
    ];
    // #endregion

    // #region METHODS
    const onSearch = searchText => {
        setSearchText(searchText);
    };

    const onRetailBrandChange = selection => {
        setFilteredRetailBrand([selection]);

        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrand =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrand
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setFilteredRetailBrand([defaultRetailBrandObj]);
        }
        setRequestsEnabled(true);
    };

    /*const handleDelete = async itemData => {
        const itemID = itemData?.id;

        if (!itemID) {
            Library.showErrorMessage("Invalid item ID!");
            return;
        }

        setRemoveID(itemID);
    }; */

    const deliveriesStatus = data => {
        const deliveries = [];
        if (data?.pickup) deliveries.push("PK");
        if (data?.courier) deliveries.push("CO");
        if (data?.shipping) deliveries.push("SH");

        const deliveriesText = deliveries.join(", ");
        return deliveriesText ? deliveriesText : "-";
    };
    const makeDeleteRequest = itemID => {
        setIsLoading(true);

        /*setTimeout(() => {
            setIsLoading(false);
            setRemoveID(null);
            Library.showSuccessMessage("Store deleted successfully!");
        }, 2000);*/

        Library.makePostRequest("deleteGroupStore", { storeID: itemID }, false, setIsLoading)
            .then(res => {
                if (res.data.status !== 200) return;
                setIsLoading(false);
                setRemoveID(null);
                Library.showSuccessMessage("Store deleted successfully!");
                props?.getUserDataFunction(localStorage.getItem("token"));
                onClickRefreshAction();
            })
            .catch(err => {
                setIsLoading(false);
                setRemoveID(null);
            });
    };

    /*const handleToogleSuspend = itemData => {
        const itemID = itemData?.id;

        if (!itemID) {
            Library.showErrorMessage("Invalid item ID!");
            return;
        }

        setToogleSuspendData(itemData);
    };*/

    const toogleSuspend = itemData => {
        setIsLoading(true);

        /*setTimeout(() => {
            setIsLoading(false);
            setToogleSuspendData(null);
            Library.showSuccessMessage("Store suspended successfully!");
        }, 2000);*/

        const groupCompanyID = localStorage.getItem("currentCompanyID");
        const itemID = itemData?.id;
        const status = itemData?.status === "Active" ? "Suspended" : "Active";

        Library.makePostRequest(
            "updateGroupStoreStatus",
            {
                groupCompanyID,
                storeID: itemID,
                status
            },
            false,
            setIsLoading
        )
            .then(res => {
                if (res.data.status !== 200) return;
                setIsLoading(false);
                setToogleSuspendData(null);
                Library.showSuccessMessage(
                    status === "Active" ? "Store activated successfully!" : "Store suspended successfully!"
                );
                const token = localStorage.getItem("token");
                props?.getUserDataFunction(token);
                onClickRefreshAction();
            })
            .catch(err => {
                setIsLoading(false);
                setToogleSuspendData(null);
            });
    };

    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
            gridRef.current?.reset?.(infiniteGridQuerykey);
        }
    };

    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <div className="flex-row gap-8px mb-16px" style={{ display: "none" /*showDevTools ? "flex" : "none"*/ }}>
                <BtnLargeSolid
                    text="Store And POS"
                    onClick={() => {
                        setShowStoreAndPOSOnboardingCompleted(true);
                    }}
                />

                <BtnLargeSolid
                    text="Store No POS"
                    onClick={() => {
                        setShowStoreWithoutPOSOnboardingCompleted(true);
                    }}
                    activeColor="var(--text-icons-orange-500)"
                    normalColor="var(--text-icons-orange-500)"
                    hoverColor="var(--text-icons-orange-500)"
                />
            </div>

            <FullScreenOverlay show={showIsStoreSavedPopup}>
                <DialogBoxComponent
                    buttonWidth={"50%"}
                    onClickYes={() => setShowIsStoreSavedPopup(false)}
                    title="You enrolled your first store!"
                    heading="CONGRATS..."
                    headingColor="#00AEEF"
                    message={`<div style= "font-size: 14px; margin: 0; line-height:14.06px">You can now continue enrolling more of your own stores or invite wholesale stores to also
                            become fulfillment centers for <span style='font-weight:600'>${labelBrandData?.name}</span>.<br> Use the menu or the left to pick your next
                            action.
                        </div>`}
                    // useImageHeader={{ src: BOStageBLogo, width: "165px" }}
                    showConfirm={() => setShowIsStoreSavedPopup(false)}
                    showCancel={false}
                    confirmText="CONTINUE"
                    confirmState="secondary"
                />
            </FullScreenOverlay>

            <FullScreenOverlay show={toogleSuspendData !== null}>
                <DialogBoxComponent
                    title={toogleSuspendData?.status === "Active" ? "Suspend Store" : "Activate Store"}
                    message={
                        toogleSuspendData?.status === "Active"
                            ? `Are you sure you want to suspend ${toogleSuspendData?.name} store?`
                            : `Are you sure you want to activate ${toogleSuspendData?.name} store?`
                    }
                    //message={`${toogleSuspendData?.storeName} status will become SUSPENDED until you reactivate the account. ${toogleSuspendData?.storeName} will be logged out of their account. This may take up to 30 minutes to take effect. No email will be sent.`}
                    onClickYes={() => {
                        toogleSuspend(toogleSuspendData);
                    }}
                    loading={isLoading}
                    loadingHeading={
                        toogleSuspendData?.status === "Active" ? "Suspending Store..." : "Activating Store..."
                    }
                    onClickNo={() => {
                        setToogleSuspendData(null);
                    }}
                />
            </FullScreenOverlay>

            <FullScreenOverlay show={removeID !== null}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-red-500)"
                    title="Removing Store"
                    message="Are you sure you want to remove this store from your account?"
                    onClickYes={() => {
                        makeDeleteRequest(removeID);
                    }}
                    loading={isLoading}
                    loadingHeading="Removing Store..."
                    onClickNo={() => {
                        setRemoveID(null);
                    }}
                />
            </FullScreenOverlay>

            {viewLayoutStyle === "Grid" && (
                <InfinityGridStoresCards
                    ref={gridRef}
                    minColumnWidth="370px"
                    limit="8"
                    //onDeleteAction={handleDelete}
                    //onSuspendAction={handleToogleSuspend}
                    queryKey={infiniteGridQuerykey}
                    statusFromPath="status"
                    onEditAction={e => {
                        navigate(`/stores/edit/${e?.ourID}`);
                    }}
                    requestEnabled={requestEnabled}
                    filters={filters}
                    editAccess={editAccess}
                />
            )}
            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="Stores"
                    columns={listColumns}
                    api={{
                        endpoint: "getStoresData",
                        useCommonPostRequest: true,
                        commonContext: "Stores",
                        formData: {
                            companyID: localStorage.getItem("currentCompanyID"),
                            limit: listNumRows,
                            ...filters
                        }
                        //onSuccess: mapListData
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{
                        onClickEdit: e => {
                            navigate(`/stores/edit/${e?.ourID}`);
                        },
                        includeActions: true,
                        extraActions: [
                            {
                                title: "Details"
                            },
                            {
                                title: "Add staff"
                            }
                        ],
                        editIsVisible: true
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "status",
                        statusColors: defaultStatusColors
                    }}
                    setLastUpdatedDateTime={setLastUpdatedDateTime}
                    rightCardTopComponent={<RightComponent lastUpdatedDateTime={lastUpdatedDateTime} />}
                    requestEnabled={requestEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </StyledPage>
    );
    // #endregion
}
// #region Small components
const RightComponent = ({ lastUpdatedDateTime }) => {
    return (
        <div
            style={{
                display: "Flex",
                gap: "16px",
                fontSize: "12px",
                textTransform: "none",
                justifyContent: "center",
                alignItems: "center"
            }}>
            <div>Total FFcenters to Replay: --</div>
            <div>Last update: {lastUpdatedDateTime}</div>
        </div>
    );
};

function setupHeaders(
    props,
    navigations,
    onClickRefreshAction,
    allRetailBrandsList,
    onRetailBrandChange,
    onFiltersLoaded,
    filteredRetailBrand,
    onSearch,
    editAccess
) {
    const createMode = navigations?.navigationAccesses?.stores?.createModeOnly;

    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Store" /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/stores", active: true, disabled: false, title: "Stores" },
        { to: "/stores/staff", active: false, disabled: true, title: "Staff" },
        { to: "/stores/pos", active: false, disabled: false, title: "POS" }
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        { to: editAccess ? "/stores/create" : "#", active: false, disabled: !editAccess, title: "Add", icon: "plus" },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" },
        { to: "#", active: false, disabled: true, title: "Edit", icon: "edit" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={
                    Library.isAdminDebug()
                        ? {
                              visible: true,
                              dropdownData: allRetailBrandsList,
                              onDropdownChange: onRetailBrandChange,
                              disabled: false,
                              type: "singleSelect",
                              selectedValue: filteredRetailBrand?.[0]?.name
                          }
                        : {
                              visible: true,
                              disabled: true
                          }
                }
                secondOrganize={{
                    visible: true,
                    disabled: true
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true,
                    enabled: false,
                    active: false,
                    outlined: false,
                    layoutComponent: <POSSetupSettingsMenuComponent />
                }}
            />
        )
    });
}

export const POSSetupSettingsMenuComponent = () => {
    const navigate = useNavigate();

    return (
        <OrganizeBtnMenu
            items={[
                {
                    name: "POS integration config",
                    value: "posSetup"
                },
                {
                    name: "POS connection settings",
                    value: "posStoreSetup"
                },
                {
                    name: "Export to POS settings",
                    value: "posExportSetup"
                }
            ]}
            onItemClick={value => {
                if (value === "posSetup") {
                    navigate("/stores/pos", { settingsButtonEnabled: true });
                }
                if (value === "posStoreSetup") {
                    navigate("/stores/pos-store", { settingsButtonEnabled: true });
                }
                if (value === "posExportSetup") {
                    navigate("/products/pos-export", { settingsButtonEnabled: true });
                }
            }}
        />
    );
};
// #endregion
